// src/LoginPage.jsx
import { useState, useEffect, useRef } from "react";
import { signIn, signUp } from "./auth";

export default function LoginPage({ onAuth }) {
  const [mode, setMode]         = useState("login"); // "login" | "signup"
  const [exploding, setExploding] = useState(false);
  const [xPos, setXPos]         = useState({top:"50%", left:"50%", size:40});
  const xRef = useRef(null);
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [mounted, setMounted]   = useState(false);
  const [forgotMode, setForgot] = useState(false); // ""|"email"|"otp"
  const [resetToken, setRToken] = useState("");
  const [forgotEmail,setFEmail] = useState("");
  const [otpInput,  setOTP]     = useState("");
  const [newPw,     setNewPw]   = useState("");
  const [showTerms, setTerms]   = useState(false);
  const [termsTab,  setTTab]    = useState("terms");
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    setMounted(true);
    // Animated particle grid background
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const resize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);

    // Particles
    const COUNT = 80;
    const particles = Array.from({ length: COUNT }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.5 + 0.4,
      opacity: Math.random() * 0.4 + 0.1,
    }));

    let mouse = { x: W / 2, y: H / 2 };
    const onMouse = e => { mouse.x = e.clientX; mouse.y = e.clientY; };
    window.addEventListener("mousemove", onMouse);

    function draw() {
      ctx.clearRect(0, 0, W, H);

      // Draw connection lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(255,60,95,${(1 - dist / 130) * 0.08})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        // Mouse attraction
        const mdx = mouse.x - particles[i].x;
        const mdy = mouse.y - particles[i].y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < 160) {
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(255,60,95,${(1 - mdist / 160) * 0.12})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }

      // Draw particles
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,60,95,${p.opacity})`;
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  function switchMode(m) {
    setMode(m);
    setError(""); setSuccess("");
    setName(""); setEmail(""); setPassword("");
    setForgot(false); setRToken(""); setFEmail(""); setOTP(""); setNewPw("");
  }

  async function handleForgot(e) {
    e.preventDefault();
    if(loading) return;
    setError(""); setLoading(true);
    try{
      const res=await fetch("/api/auth/forgot",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:forgotEmail.trim()})});
      const d=await res.json();
      if(res.ok&&d.resetToken){ setRToken(d.resetToken); setForgot("otp"); }
      else if(res.ok){ setForgot("otp"); } // still show OTP step (security)
      else setError(d.error||"Failed to send OTP");
    }catch{ setError("Network error"); }
    finally{ setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault();
    if(loading) return;
    setError(""); setLoading(true);
    try{
      const res=await fetch("/api/auth/reset",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({resetToken,otp:otpInput,newPassword:newPw})});
      const d=await res.json();
      if(res.ok){ setSuccess("Password reset! Please sign in."); setForgot(false); setMode("login"); }
      else setError(d.error||"Reset failed");
    }catch{ setError("Network error"); }
    finally{ setLoading(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const result = mode === "login"
        ? await signIn({ email, password })
        : await signUp({ name, email, password });

      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(mode === "login" ? "Welcome back!" : "Account created!");
        // Measure exact position of the X in the logo before animating
        if (xRef.current) {
          const r = xRef.current.getBoundingClientRect();
          setXPos({ top: r.top + r.height/2, left: r.left + r.width/2, size: r.height });
        }
        setExploding(true);
        setTimeout(() => onAuth(result.user, result.needsVerification), 5000);
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "linear-gradient(135deg,#06060e 0%,#0d0614 50%,#06060e 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Syne','Segoe UI',sans-serif",
      overflow: exploding ? "visible" : "hidden",
    }}>
      {/* Help icon — top right */}
      <div style={{position:"fixed",top:14,right:16,zIndex:9998}}>
        <button onClick={()=>setShowHelp(h=>!h)}
          style={{width:34,height:34,borderRadius:"50%",
            background:showHelp?"rgba(255,60,95,.15)":"rgba(255,255,255,.07)",
            border:showHelp?"1px solid rgba(255,60,95,.3)":"1px solid rgba(255,255,255,.15)",
            display:"flex",alignItems:"center",justifyContent:"center",
            color:showHelp?"#ff3c5f":"rgba(255,255,255,.6)",fontSize:"1em",fontWeight:700,cursor:"pointer",
            transition:"background .2s,color .2s"}}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,60,95,.15)";e.currentTarget.style.color="#ff3c5f";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,.07)";e.currentTarget.style.color="rgba(255,255,255,.6)";}}>
          ?
        </button>
        {showHelp&&(
          <div style={{position:"absolute",top:42,right:0,background:"#0f0f1e",border:"1px solid rgba(255,255,255,.1)",
            borderRadius:12,padding:"14px 18px",minWidth:220,boxShadow:"0 8px 32px rgba(0,0,0,.5)"}}>
            <div style={{fontSize:".72em",letterSpacing:"1.5px",color:"rgba(255,255,255,.35)",marginBottom:6,fontWeight:700}}>NEED HELP?</div>
            <div style={{fontSize:".82em",color:"rgba(255,255,255,.6)",lineHeight:1.5,marginBottom:10}}>Queries or complaints:</div>
            <a href="mailto:rajarshipaul4others@gmail.com"
              style={{fontSize:".78em",color:"#ff3c5f",textDecoration:"none",fontWeight:600,wordBreak:"break-all"}}>
              rajarshipaul4others@gmail.com
            </a>
          </div>
        )}
      </div>
      {/* Explosion overlay — X spins from logo to center then zooms */}
      {exploding && (
        <div style={{position:"fixed",inset:0,zIndex:9999,pointerEvents:"none"}}>
          <style>{`
            @keyframes xMove {
              0%   { top:${xPos.top}px; left:${xPos.left}px; }
              35%  { top:50vh; left:50vw; }
              100% { top:50vh; left:50vw; }
            }
            @keyframes bgPulse {
              0%,30% { background:transparent; }
              55%    { background:rgba(255,60,95,.08); }
              75%    { background:rgba(255,60,95,.25); }
              90%    { background:rgba(255,60,95,.6); }
              100%   { background:#07070f; }
            }
            /* Real lightning: instant on, rapid flicker, instant off */
            @keyframes bolt1 {
              0%,43%   { opacity:0; }
              44%      { opacity:1; }
              44.5%    { opacity:.3; }
              45%      { opacity:1; }
              45.8%    { opacity:0; }
              60%      { opacity:0; }
              60.5%    { opacity:1; }
              61%      { opacity:.2; }
              61.5%    { opacity:1; }
              62.5%    { opacity:0; }
              100%     { opacity:0; }
            }
            @keyframes bolt2 {
              0%,47%   { opacity:0; }
              48%      { opacity:1; }
              48.6%    { opacity:.15; }
              49%      { opacity:1; }
              50%      { opacity:0; }
              63%      { opacity:0; }
              63.5%    { opacity:1; }
              64.2%    { opacity:.25; }
              64.8%    { opacity:1; }
              65.8%    { opacity:0; }
              100%     { opacity:0; }
            }
            @keyframes bolt3 {
              0%,52%   { opacity:0; }
              53%      { opacity:1; }
              53.5%    { opacity:.1; }
              54%      { opacity:1; }
              54.3%    { opacity:.4; }
              54.8%    { opacity:1; }
              56%      { opacity:0; }
              69%      { opacity:0; }
              69.5%    { opacity:1; }
              70.2%    { opacity:.2; }
              70.8%    { opacity:1; }
              72%      { opacity:0; }
              100%     { opacity:0; }
            }
            @keyframes bolt4 {
              0%,57%   { opacity:0; }
              58%      { opacity:1; }
              58.8%    { opacity:.3; }
              59.2%    { opacity:1; }
              60.2%    { opacity:0; }
              73%      { opacity:0; }
              73.6%    { opacity:1; }
              74.4%    { opacity:0; }
              100%     { opacity:0; }
            }
            @keyframes flashScreen {
              0%,43%   { opacity:0; }
              44%      { opacity:.35; }
              45.5%    { opacity:0; }
              47.5%    { opacity:.25; }
              49%      { opacity:0; }
              52.5%    { opacity:.4; }
              54.5%    { opacity:0; }
              57.5%    { opacity:.3; }
              59.5%    { opacity:0; }
              63%      { opacity:.2; }
              65%      { opacity:0; }
              69%      { opacity:.35; }
              71%      { opacity:0; }
              73%      { opacity:.25; }
              74.5%    { opacity:0; }
              100%     { opacity:0; }
            }
          `}</style>
          {/* Screen flash — white pulse on each strike */}
          <div style={{position:"fixed",inset:0,background:"rgba(255,220,220,1)",animation:"flashScreen 5s linear forwards",pointerEvents:"none",zIndex:1}}/>

          {/* BOLT 1 — left side, main + 3 full-length branches */}
          <svg style={{position:"fixed",top:0,left:"18%",animation:"bolt1 5s linear forwards",pointerEvents:"none",zIndex:2,filter:"drop-shadow(0 0 4px #fff) drop-shadow(0 0 12px #ff3c5f) drop-shadow(0 0 30px #ff0000)"}} width="160" height="100vh" viewBox="0 0 160 800" preserveAspectRatio="none">
            <path d="M80,0 L68,90 L82,90 L58,210 L74,210 L44,370 L62,370 L32,560 L52,560 L28,800" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinejoin="round"/>
            <path d="M80,0 L68,90 L82,90 L58,210 L74,210 L44,370 L62,370 L32,560 L52,560 L28,800" fill="none" stroke="#ff3c5f" strokeWidth="6" strokeLinejoin="round" opacity="0.7"/>
            {/* branch 1 — forks at 210, goes full length to bottom-left */}
            <path d="M58,210 L34,290 L14,390 L4,490 L18,580 L6,680 L0,800" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinejoin="round"/>
            <path d="M58,210 L34,290 L14,390 L4,490 L18,580 L6,680 L0,800" fill="none" stroke="#ff3c5f" strokeWidth="4" strokeLinejoin="round" opacity="0.6"/>
            {/* branch 1 sub-fork at 390 */}
            <path d="M14,390 L32,460 L22,530 L36,610 L24,700 L30,800" fill="none" stroke="#ffffff" strokeWidth="1.2" strokeLinejoin="round" opacity="0.75"/>
            <path d="M14,390 L32,460 L22,530 L36,610 L24,700 L30,800" fill="none" stroke="#ff6080" strokeWidth="3" strokeLinejoin="round" opacity="0.45"/>
            {/* branch 2 — forks at 370, goes right to bottom */}
            <path d="M62,370 L90,440 L108,520 L96,610 L112,700 L100,800" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" opacity="0.8"/>
            <path d="M62,370 L90,440 L108,520 L96,610 L112,700 L100,800" fill="none" stroke="#ff3c5f" strokeWidth="3.5" strokeLinejoin="round" opacity="0.5"/>
            {/* branch 3 — early fork at 90, thin */}
            <path d="M82,90 L100,160 L88,240 L104,320 L90,410 L106,500 L94,600 L110,800" fill="none" stroke="#ffffff" strokeWidth="1" strokeLinejoin="round" opacity="0.65"/>
            <path d="M82,90 L100,160 L88,240 L104,320 L90,410 L106,500 L94,600 L110,800" fill="none" stroke="#ff6080" strokeWidth="2.5" strokeLinejoin="round" opacity="0.4"/>
          </svg>

          {/* BOLT 2 — right side, main + 3 full-length branches */}
          <svg style={{position:"fixed",top:0,right:"10%",animation:"bolt2 5s linear forwards",pointerEvents:"none",zIndex:2,filter:"drop-shadow(0 0 4px #fff) drop-shadow(0 0 12px #ff3c5f) drop-shadow(0 0 28px #ff0000)"}} width="160" height="100vh" viewBox="0 0 160 800" preserveAspectRatio="none">
            <path d="M70,0 L84,100 L66,100 L88,230 L70,230 L94,400 L72,400 L96,590 L74,590 L90,800" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinejoin="round"/>
            <path d="M70,0 L84,100 L66,100 L88,230 L70,230 L94,400 L72,400 L96,590 L74,590 L90,800" fill="none" stroke="#ff3c5f" strokeWidth="6" strokeLinejoin="round" opacity="0.7"/>
            {/* branch right — forks at 230, runs to bottom-right */}
            <path d="M88,230 L114,310 L130,400 L118,490 L136,570 L122,660 L140,800" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinejoin="round"/>
            <path d="M88,230 L114,310 L130,400 L118,490 L136,570 L122,660 L140,800" fill="none" stroke="#ff3c5f" strokeWidth="4" strokeLinejoin="round" opacity="0.58"/>
            {/* sub-fork of right branch at 400 */}
            <path d="M130,400 L148,460 L138,530 L152,620 L144,720 L158,800" fill="none" stroke="#ffffff" strokeWidth="1" strokeLinejoin="round" opacity="0.7"/>
            <path d="M130,400 L148,460 L138,530 L152,620 L144,720 L158,800" fill="none" stroke="#ff6080" strokeWidth="2.5" strokeLinejoin="round" opacity="0.4"/>
            {/* branch left — forks at 400, runs to bottom-left */}
            <path d="M72,400 L50,470 L36,560 L48,640 L30,730 L42,800" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" opacity="0.8"/>
            <path d="M72,400 L50,470 L36,560 L48,640 L30,730 L42,800" fill="none" stroke="#ff3c5f" strokeWidth="3.5" strokeLinejoin="round" opacity="0.5"/>
            {/* thin early branch at 100 */}
            <path d="M66,100 L48,170 L60,250 L44,340 L56,430 L40,530 L52,630 L38,800" fill="none" stroke="#ffffff" strokeWidth="1" strokeLinejoin="round" opacity="0.6"/>
          </svg>

          {/* BOLT 3 removed — was blocking X animation */}

          {/* BOLT 4 — far left, full branches to bottom */}
          <svg style={{position:"fixed",top:0,left:"3%",animation:"bolt4 5s linear forwards",pointerEvents:"none",zIndex:2,filter:"drop-shadow(0 0 3px #fff) drop-shadow(0 0 10px #ff3c5f) drop-shadow(0 0 22px #ff0000)"}} width="130" height="100vh" viewBox="0 0 130 800" preserveAspectRatio="none">
            <path d="M65,0 L54,70 L70,70 L46,180 L62,180 L38,330 L56,330 L28,520 L48,520 L24,800" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinejoin="round"/>
            <path d="M65,0 L54,70 L70,70 L46,180 L62,180 L38,330 L56,330 L28,520 L48,520 L24,800" fill="none" stroke="#ff3c5f" strokeWidth="5" strokeLinejoin="round" opacity="0.6"/>
            {/* left branch — forks at 180, runs full to bottom */}
            <path d="M46,180 L22,260 L6,360 L18,460 L2,560 L14,670 L0,800" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M46,180 L22,260 L6,360 L18,460 L2,560 L14,670 L0,800" fill="none" stroke="#ff3c5f" strokeWidth="3.5" strokeLinejoin="round" opacity="0.55"/>
            {/* sub-fork at 360 */}
            <path d="M6,360 L24,430 L14,510 L28,600 L16,700 L30,800" fill="none" stroke="#ffffff" strokeWidth="1" strokeLinejoin="round" opacity="0.65"/>
            {/* right branch — forks at 330, runs to bottom-right */}
            <path d="M56,330 L80,410 L96,500 L82,590 L100,680 L88,800" fill="none" stroke="#ffffff" strokeWidth="1.4" strokeLinejoin="round" opacity="0.72"/>
            <path d="M56,330 L80,410 L96,500 L82,590 L100,680 L88,800" fill="none" stroke="#ff3c5f" strokeWidth="3" strokeLinejoin="round" opacity="0.48"/>
            {/* thin early branch at 70 */}
            <path d="M70,70 L88,140 L76,220 L92,310 L78,400 L94,500 L80,610 L96,800" fill="none" stroke="#ffffff" strokeWidth="0.8" strokeLinejoin="round" opacity="0.55"/>
          </svg>
          <div style={{position:"fixed",inset:0,animation:"bgPulse 5s ease forwards"}}/>
          <span style={{
            fontFamily:"'Syne',sans-serif", fontWeight:800,
            fontSize: xPos.size,
            color:"#ff3c5f",
            position:"fixed",
            top: xPos.top, left: xPos.left,
            transformOrigin:"center",
            lineHeight:1,
            animation:"xMove 5s cubic-bezier(.4,0,.2,1) forwards, xExplode 5s cubic-bezier(.4,0,.2,1) forwards",
          }}>X</span>
        </div>
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap');

        .auth-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 11px;
          color: #e8e8f2;
          padding: 13px 16px;
          font-family: 'Syne', sans-serif;
          font-size: 0.88em;
          outline: none;
          transition: border-color 0.2s, background 0.2s;
          box-sizing: border-box;
        }
        .auth-input:focus {
          border-color: rgba(255,60,95,0.6);
          background: rgba(255,255,255,0.07);
        }
        .auth-input::placeholder { color: rgba(255,255,255,0.22); }

        .auth-btn {
          width: 100%;
          background: linear-gradient(135deg, #ff3c5f, #9b1dff);
          border: none;
          color: #fff;
          padding: 14px;
          border-radius: 11px;
          cursor: pointer;
          font-family: 'Syne', sans-serif;
          font-size: 0.9em;
          font-weight: 700;
          letter-spacing: 0.3px;
          transition: all 0.22s;
          position: relative;
          overflow: hidden;
        }
        .auth-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 36px rgba(255,60,95,0.38);
        }
        .auth-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .auth-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg,rgba(255,255,255,0.1),transparent);
          opacity: 0;
          transition: opacity 0.2s;
        }
        .auth-btn:hover::after { opacity: 1; }

        .mode-tab {
          flex: 1;
          padding: 9px;
          border: none;
          background: transparent;
          color: rgba(255,255,255,0.38);
          cursor: pointer;
          font-family: 'Syne', sans-serif;
          font-size: 0.82em;
          font-weight: 700;
          border-radius: 8px;
          transition: all 0.18s;
        }
        .mode-tab.active {
          background: rgba(255,60,95,0.15);
          color: #ff3c5f;
        }
        .mode-tab:hover:not(.active) { color: rgba(255,255,255,0.65); }

        .card-enter {
          animation: cardIn 0.5s cubic-bezier(0.22,1,0.36,1);
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(28px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .logo-glow {
          animation: logoPulse 3s ease-in-out infinite;
        }
        @keyframes logoPulse {
          0%,100% { text-shadow: 0 0 20px rgba(255,60,95,0.4), 0 0 40px rgba(155,29,255,0.2); }
          50%      { text-shadow: 0 0 40px rgba(255,60,95,0.7), 0 0 70px rgba(155,29,255,0.35); }
        }
        @keyframes xExplode {
          0%   { transform:translate(-50%,-50%) scale(1) rotate(0deg);    opacity:1; text-shadow:none; }
          15%  { transform:translate(-50%,-50%) scale(1.6) rotate(120deg); opacity:1; text-shadow:0 0 20px #ff3c5f; }
          30%  { transform:translate(-50%,-50%) scale(1.2) rotate(360deg); opacity:1; text-shadow:0 0 40px #ff3c5f, 0 0 80px rgba(255,60,95,.5); }
          45%  { transform:translate(-50%,-50%) scale(1.4) rotate(360deg); opacity:1; text-shadow:0 0 60px #ff3c5f, 0 0 120px rgba(255,60,95,.6); }
          65%  { transform:translate(-50%,-50%) scale(6)  rotate(360deg);  opacity:1; text-shadow:0 0 80px #ff3c5f, 0 0 160px rgba(255,60,95,.8); }
          82%  { transform:translate(-50%,-50%) scale(18) rotate(360deg);  opacity:1; text-shadow:0 0 120px #ff3c5f; }
          95%  { transform:translate(-50%,-50%) scale(35) rotate(360deg);  opacity:.6; }
          100% { transform:translate(-50%,-50%) scale(50) rotate(360deg);  opacity:0; }
        }

        .field-label {
          font-size: 0.68em;
          color: rgba(255,255,255,0.3);
          letter-spacing: 1.5px;
          text-transform: uppercase;
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
        }

        .pw-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(255,255,255,0.3);
          cursor: pointer;
          font-size: 0.82em;
          padding: 4px;
          transition: color 0.18s;
        }
        .pw-toggle:hover { color: rgba(255,255,255,0.6); }

        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-8px); }
          40%      { transform: translateX(8px); }
          60%      { transform: translateX(-5px); }
          80%      { transform: translateX(5px); }
        }
        .shake { animation: shake 0.4s ease; }

        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; display: inline-block; }
      `}</style>

      {/* Canvas background */}
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />

      {/* Radial glow blobs */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,60,95,0.07) 0%,transparent 70%)", top: "-10%", left: "-10%", animation: "logoPulse 6s ease-in-out infinite" }} />
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(155,29,255,0.06) 0%,transparent 70%)", bottom: "-5%", right: "5%", animation: "logoPulse 8s ease-in-out infinite reverse" }} />
      </div>

      {/* Auth card */}
      <div className="card-enter" style={{
        position: "relative", zIndex: 10,
        width: "100%", maxWidth: 420,
        margin: "0 20px",
        opacity: exploding ? 0 : 1,
        transition: "opacity 0.3s ease",
        background: "rgba(12,12,22,0.85)",
        backdropFilter: "blur(30px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 22,
        padding: "36px 36px 32px",
        boxShadow: "0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div className="logo-glow" style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "2.2em", color: "#fff", letterSpacing: "-1px", marginBottom: 4 }}>
            NE<span ref={xRef} style={{ color: "#ff3c5f", display:"inline-block",
              opacity: exploding ? 0 : 1, transition:"opacity 0.1s"
            }}>X</span>US
          </div>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontStyle: "italic", fontSize: "0.9em", color: "rgba(255,255,255,0.35)",
            opacity: exploding ? 0 : 1, transition:"opacity .3s" }}>
            {mode === "login" ? "Your AI Hub" : "Create your account"}
          </div>
        </div>

        {/* Mode tabs */}
        <div style={{ display: "flex", gap: 3, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 11, padding: 4, marginBottom: 24 }}>
          <button className={`mode-tab ${mode === "login" ? "active" : ""}`} onClick={() => switchMode("login")}>Sign In</button>
          <button className={`mode-tab ${mode === "signup" ? "active" : ""}`} onClick={() => switchMode("signup")}>Create Account</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {mode === "signup" && (
            <div style={{ marginBottom: 16 }}>
              <label className="field-label">Full Name</label>
              <input
                className="auth-input"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Jane Doe"
                autoComplete="name"
                required
              />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label className="field-label">Email Address</label>
            <input
              className="auth-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="jane@example.com"
              autoComplete="email"
              required
            />
          </div>

          <div style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="field-label" style={{ margin: 0 }}>Password</label>
              {mode === "login" && (
                <button type="button" onClick={() => { setForgot("email"); setError(""); setSuccess(""); }}
                  style={{ background: "none", border: "none", color: "rgba(255,60,95,0.7)", fontSize: "0.72em", cursor: "pointer", fontFamily: "'Syne',sans-serif" }}>
                  Forgot password?
                </button>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <input
                className="auth-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "Min. 6 characters" : "Your password"}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                style={{ paddingRight: 44 }}
                required
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPw(p => !p)}>
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {/* Error / Success */}
          {error && (
            <div className="shake" style={{ background: "rgba(255,60,95,0.1)", border: "1px solid rgba(255,60,95,0.3)", borderRadius: 9, padding: "10px 14px", marginBottom: 16, fontSize: "0.82em", color: "#ff6b85", display: "flex", alignItems: "center", gap: 8 }}>
              <span>⚠</span> {error}
            </div>
          )}
          {success && (
            <div style={{ background: "rgba(168,255,120,0.1)", border: "1px solid rgba(168,255,120,0.3)", borderRadius: 9, padding: "10px 14px", marginBottom: 16, fontSize: "0.82em", color: "#a8ff78", display: "flex", alignItems: "center", gap: 8 }}>
              <span>✓</span> {success}
            </div>
          )}

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading
              ? <><span className="spin">↻</span> {mode === "login" ? " Signing in…" : " Creating account…"}</>
              : mode === "login" ? "Sign In →" : "Create Account →"
            }
          </button>
        </form>

        {/* Password strength indicator (signup only) */}
        {mode === "signup" && password.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
              {[1,2,3,4].map(i => {
                const strength = password.length < 6 ? 1 : password.length < 8 ? 2 : /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4 : 3;
                const colors = ["#ff3c5f","#ff9500","#ffd166","#a8ff78"];
                return <div key={i} style={{ flex: 1, height: 3, borderRadius: 3, background: i <= strength ? colors[strength-1] : "rgba(255,255,255,0.08)", transition: "all 0.3s" }} />;
              })}
            </div>
            <div style={{ fontSize: "0.68em", color: "rgba(255,255,255,0.3)" }}>
              {password.length < 6 ? "Too short" : password.length < 8 ? "Weak" : /[A-Z]/.test(password) && /[0-9]/.test(password) ? "Strong ✓" : "Good"}
            </div>
          </div>
        )}
      </div>

      {/* Bottom tagline + Terms link */}
      <div style={{ position: "absolute", bottom: 24, left: 0, right: 0, textAlign: "center", fontSize: "0.7em", color: "rgba(255,255,255,0.15)", letterSpacing: "2px", textTransform: "uppercase" }}>
        NEXUS · AI Platform ·{" "}
        <button type="button" onClick={()=>setTerms(true)} style={{background:"none",border:"none",color:"rgba(255,60,95,.4)",fontSize:"1em",cursor:"pointer",fontFamily:"inherit",letterSpacing:"2px",textTransform:"uppercase"}}>
          Terms &amp; Privacy
        </button>
      </div>

      {/* ── Forgot Password Modal ───────────────────────── */}
      {forgotMode && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={()=>{setForgot(false);setError("");}}>
          <div style={{background:"#0d0d1e",border:"1px solid rgba(255,60,95,.25)",borderRadius:18,padding:"28px 28px",maxWidth:400,width:"100%",fontFamily:"'Syne',sans-serif",boxShadow:"0 40px 80px rgba(0,0,0,.7)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:"1.1em",fontWeight:800,color:"#fff",marginBottom:6}}>🔑 Reset Password</div>
            <div style={{fontSize:".75em",color:"rgba(255,255,255,.35)",marginBottom:20}}>
              {forgotMode==="email"?"Enter your email to receive a 6-digit OTP":"Enter the OTP sent to your email + your new password"}
            </div>
            {forgotMode==="email" ? (
              <form onSubmit={handleForgot}>
                <input className="auth-input" type="email" value={forgotEmail} onChange={e=>setFEmail(e.target.value)}
                  placeholder="your@email.com" required style={{marginBottom:16}}/>
                {error&&<div style={{color:"#ff6b85",fontSize:".78em",marginBottom:12}}>⚠ {error}</div>}
                <button type="submit" className="auth-btn" disabled={loading}>{loading?"Sending OTP…":"Send OTP →"}</button>
                <button type="button" onClick={()=>{setForgot(false);setError("");}} style={{width:"100%",background:"none",border:"none",color:"rgba(255,255,255,.25)",marginTop:10,cursor:"pointer",fontFamily:"'Syne',sans-serif",fontSize:".8em"}}>Cancel</button>
              </form>
            ) : (
              <form onSubmit={handleReset}>
                <input className="auth-input" type="text" value={otpInput} onChange={e=>setOTP(e.target.value)}
                  placeholder="6-digit OTP" maxLength={6} required style={{marginBottom:12,letterSpacing:"8px",textAlign:"center",fontSize:"1.2em"}}/>
                <input className="auth-input" type="password" value={newPw} onChange={e=>setNewPw(e.target.value)}
                  placeholder="New password (min 6 chars)" required style={{marginBottom:16}}/>
                {error&&<div style={{color:"#ff6b85",fontSize:".78em",marginBottom:12}}>⚠ {error}</div>}
                <button type="submit" className="auth-btn" disabled={loading}>{loading?"Resetting…":"Reset Password →"}</button>
                <button type="button" onClick={()=>setForgot("email")} style={{width:"100%",background:"none",border:"none",color:"rgba(255,255,255,.25)",marginTop:10,cursor:"pointer",fontFamily:"'Syne',sans-serif",fontSize:".8em"}}>← Back</button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Terms & Privacy Modal ───────────────────────── */}
      {showTerms && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={()=>setTerms(false)}>
          <div style={{background:"#0d0d1e",border:"1px solid rgba(255,255,255,.1)",borderRadius:18,width:"100%",maxWidth:560,maxHeight:"85vh",overflowY:"auto",fontFamily:"'Syne',sans-serif",boxShadow:"0 40px 80px rgba(0,0,0,.8)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{padding:"20px 24px 14px",borderBottom:"1px solid rgba(255,255,255,.07)",position:"sticky",top:0,background:"#0d0d1e",borderRadius:"18px 18px 0 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",gap:4,background:"rgba(255,255,255,.04)",borderRadius:9,padding:3}}>
                {["terms","privacy"].map(t=>(
                  <button key={t} onClick={()=>setTTab(t)} style={{background:termsTab===t?"rgba(255,60,95,.15)":"none",border:termsTab===t?"1px solid rgba(255,60,95,.3)":"1px solid transparent",color:termsTab===t?"#ff3c5f":"rgba(255,255,255,.4)",borderRadius:7,padding:"5px 14px",cursor:"pointer",fontFamily:"'Syne',sans-serif",fontSize:".75em",fontWeight:700}}>
                    {t==="terms"?"Terms of Use":"Privacy Policy"}
                  </button>
                ))}
              </div>
              <button onClick={()=>setTerms(false)} style={{background:"rgba(255,255,255,.07)",border:"1px solid rgba(255,255,255,.1)",color:"rgba(255,255,255,.5)",borderRadius:7,width:28,height:28,cursor:"pointer",fontSize:".9em",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
            <div style={{padding:"20px 24px 24px",fontSize:".8em",color:"rgba(255,255,255,.55)",lineHeight:1.8}}>
              {termsTab==="terms" ? (<>
                <div style={{fontWeight:800,color:"#fff",fontSize:"1.1em",marginBottom:12}}>Terms of Use</div>
                <p style={{marginBottom:12}}><strong style={{color:"rgba(255,255,255,.8)"}}>Last updated:</strong> {new Date().toLocaleDateString("en-IN",{year:"numeric",month:"long",day:"numeric"})}</p>
                <p style={{marginBottom:12}}>By accessing NEXUS, you agree to use this platform responsibly and in accordance with all applicable laws.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>1. Use of Service</div>
                <p style={{marginBottom:12}}>NEXUS provides AI-powered tools for personal and professional use. You may not use the platform to generate harmful, illegal, or misleading content.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>2. Accounts</div>
                <p style={{marginBottom:12}}>You are responsible for maintaining the confidentiality of your account credentials. We reserve the right to suspend accounts that violate these terms.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>3. Payments</div>
                <p style={{marginBottom:12}}>Subscription fees are billed monthly. Payments are non-refundable except as required by law. Usage limits reset on the 1st of each month.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>4. Limitation of Liability</div>
                <p style={{marginBottom:12}}>NEXUS is provided "as is" without warranties. We are not liable for any indirect or incidental damages arising from your use of the platform.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>5. Changes</div>
                <p>We may update these terms at any time. Continued use of NEXUS after changes constitutes acceptance of the new terms.</p>
              </>) : (<>
                <div style={{fontWeight:800,color:"#fff",fontSize:"1.1em",marginBottom:12}}>Privacy Policy</div>
                <p style={{marginBottom:12}}><strong style={{color:"rgba(255,255,255,.8)"}}>Last updated:</strong> {new Date().toLocaleDateString("en-IN",{year:"numeric",month:"long",day:"numeric"})}</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>1. Data We Collect</div>
                <p style={{marginBottom:12}}>We collect your name, email address, and usage data (number of requests per module). We do not collect sensitive personal information.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>2. How We Use Your Data</div>
                <p style={{marginBottom:12}}>Your data is used solely to provide NEXUS services — account management, usage tracking, and sending transactional emails. We do not sell your data.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>3. AI Inputs</div>
                <p style={{marginBottom:12}}>Messages you send to AI modules are processed by Google Gemini API. Please review <a href="https://ai.google.dev/terms" target="_blank" rel="noreferrer" style={{color:"#ff3c5f"}}>Google's AI terms</a> for details on how prompts are handled.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>4. Data Storage</div>
                <p style={{marginBottom:12}}>Your data is stored in a secure MySQL database. Study chat history is stored to enable cross-device sync. You may delete your account at any time from profile settings.</p>
                <div style={{fontWeight:700,color:"rgba(255,255,255,.8)",marginBottom:6,marginTop:16}}>5. Contact</div>
                <p>For privacy requests, contact: <a href="mailto:rajarshipaul56@gmail.com" style={{color:"#ff3c5f"}}>rajarshipaul56@gmail.com</a></p>
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* About — bottom right corner */}
      <div style={{ position:"absolute", bottom:20, right:24, textAlign:"right", lineHeight:1.7 }}>
        <div style={{ fontSize:".65em", color:"#ffffff", letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:3 }}>Founder</div>
        <div style={{ fontSize:".78em", fontWeight:700, color:"#ffffff", fontFamily:"'Syne',sans-serif" }}>Rajarshi Paul</div>
        <a href="mailto:rajarshipaul56@gmail.com" style={{ display:"block", fontSize:".68em", color:"rgba(255,60,95,.5)", textDecoration:"none", transition:"color .2s" }}
          onMouseEnter={e=>e.target.style.color="#ff3c5f"} onMouseLeave={e=>e.target.style.color="rgba(255,60,95,.5)"}>
          rajarshipaul56@gmail.com
        </a>
        <a href="https://www.linkedin.com/in/rajarshi-paul-a0710628b/" target="_blank" rel="noreferrer"
          style={{ display:"block", fontSize:".68em", color:"rgba(255,60,95,.5)", textDecoration:"none", transition:"color .2s" }}
          onMouseEnter={e=>e.target.style.color="#ff3c5f"} onMouseLeave={e=>e.target.style.color="rgba(255,60,95,.5)"}>
          LinkedIn →
        </a>
      </div>

      {/* Subtle Privacy Policy corner link */}
      <button
        type="button"
        onClick={()=>{ setTTab("privacy"); setTerms(true); }}
        style={{
          position:"absolute",
          bottom:10,
          left:14,
          fontSize:".6em",
          color:"#ffffff",
          background:"none",
          border:"none",
          padding:0,
          cursor:"pointer",
          letterSpacing:"1.5px",
          textTransform:"uppercase",
        }}
      >
        Privacy Policy
      </button>
    </div>
  );
}
